<script lang="ts">
  /**
   * MobileAgendaView - Calendar › Agenda on phones (plans/mobile-v2.md §3.6):
   * the events of the next 90 days grouped by day (days without events are left
   * out), in the same rows as Month's selected day. A multi-day event is listed
   * once, on the day it starts.
   */
  import type { CalendarEvent } from '../../lib/types';
  import { getTodayStr } from '../../lib/utils';
  import { openEventModal, openEventModalForEdit } from '../../lib/stores/ui';
  import { AGENDA_DAYS, buildAgendaGroups, formatDayHeading } from '../../lib/mobileCalendar';
  import FabButton from './FabButton.svelte';
  import MobileEventRow from './MobileEventRow.svelte';

  interface Props {
    events: CalendarEvent[];
  }

  let { events }: Props = $props();

  let today = $derived(getTodayStr());
  let groups = $derived(buildAgendaGroups(events, today));
</script>

<div class="agenda">
  {#each groups as group (group.date)}
    <section class="group" aria-label={formatDayHeading(group.date)}>
      <h2 class:today={group.relative === 'Today'}>
        {formatDayHeading(group.date)}
        {#if group.relative}<span class="rel">{group.relative}</span>{/if}
      </h2>
      {#each group.events as event (event.id)}
        <MobileEventRow {event} onSelect={openEventModalForEdit} />
      {/each}
    </section>
  {:else}
    <div class="none">
      <p>Nothing scheduled in the next {AGENDA_DAYS} days</p>
      <button type="button" class="add" onclick={() => openEventModal(today)}>+ Add event</button>
    </div>
  {/each}
  {#if groups.length}
    <p class="foot">Showing the next {AGENDA_DAYS} days</p>
  {/if}

  <FabButton label="Add event" onClick={() => openEventModal(today)} />
</div>

<style>
  .agenda {
    height: 100%;
    min-width: 0;
    padding-bottom: 96px; /* room for the "+" button */
    overflow-y: auto;
    overscroll-behavior: contain;
    background: #f9fafb;
  }

  h2 {
    position: sticky;
    top: 0;
    z-index: 2;
    display: flex;
    gap: 8px;
    align-items: baseline;
    margin: 0;
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 700;
    color: #374151;
    background: #f3f4f6;
    border-top: 1px solid #e5e7eb;
    border-bottom: 1px solid #e5e7eb;
  }

  h2.today {
    color: #2563eb;
  }

  .rel {
    font-weight: 600;
    color: #6b7280;
  }

  .none {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 48px 16px;
    text-align: center;
    color: #6b7280;
  }

  .none p {
    margin: 0 0 8px;
    font-size: 14.5px;
  }

  .add {
    min-height: 44px;
    padding: 0 12px;
    font: inherit;
    font-weight: 600;
    color: #2563eb;
    background: none;
    border: 0;
    cursor: pointer;
  }

  .foot {
    margin: 0;
    padding: 16px;
    text-align: center;
    font-size: 13px;
    color: #9ca3af;
  }
</style>
